import { useEffect, useState } from "react";

import { api, type DecisionDTO } from "../lib/api.ts";

const CATEGORY_LABELS: Record<string, string> = {
	"strategic": "Strategic",
	"operational": "Operational",
	"venture-gate": "Venture gate",
	"cash-flow": "Cash flow",
	"personnel": "Personnel",
	"business-status": "Business status",
	"other": "Other",
};

const CATEGORY_COLORS: Record<string, string> = {
	"strategic": "bg-purple-500/15 text-purple-300 border-purple-500/30",
	"operational": "bg-blue-500/15 text-blue-300 border-blue-500/30",
	"venture-gate": "bg-amber-500/15 text-amber-300 border-amber-500/30",
	"cash-flow": "bg-red-500/15 text-red-300 border-red-500/30",
	"personnel": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
	"business-status": "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
	"other": "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

export function Decisions() {
	const [decisions, setDecisions] = useState<DecisionDTO[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [filter, setFilter] = useState<{ business: string; dueOnly: boolean }>({
		business: "",
		dueOnly: false,
	});

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const res = await api.listDecisions({
					business: filter.business || undefined,
					dueOnly: filter.dueOnly || undefined,
					limit: 100,
				});
				if (!cancelled) setDecisions(res.decisions);
			} catch (err) {
				if (!cancelled) setError((err as Error).message);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [filter]);

	const overdue = decisions?.filter((d) => {
		if (!d.followUpDate) return false;
		return new Date(d.followUpDate) < new Date();
	}).length;

	return (
		<div className="px-6 py-8">
			<div className="flex items-baseline justify-between mb-6">
				<div>
					<h1 className="text-xl font-semibold">Decisions log</h1>
					<p className="text-sm text-zinc-500 mt-1">
						{decisions === null
							? "Loading…"
							: `${decisions.length} decision${decisions.length === 1 ? "" : "s"}${overdue ? ` · ${overdue} overdue follow-up${overdue === 1 ? "" : "s"}` : ""}`}
					</p>
				</div>
				<div className="flex gap-3 text-sm">
					<input
						value={filter.business}
						onChange={(e) => setFilter((f) => ({ ...f, business: e.target.value }))}
						placeholder="filter by business id (e.g. g1-mining)"
						className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-zinc-100 placeholder:text-zinc-500 w-64"
					/>
					<label className="flex items-center gap-2 text-zinc-400 cursor-pointer">
						<input
							type="checkbox"
							checked={filter.dueOnly}
							onChange={(e) => setFilter((f) => ({ ...f, dueOnly: e.target.checked }))}
						/>
						upcoming only
					</label>
				</div>
			</div>

			{error && (
				<div className="p-3 border border-red-500/30 rounded bg-red-500/10 text-sm text-red-300 mb-4">
					{error}
				</div>
			)}

			{decisions !== null && decisions.length === 0 && !error && (
				<div className="text-center text-zinc-500 mt-16 text-sm">
					No decisions yet. Tell the agent: "Save decision: ..." in chat.
				</div>
			)}

			<div className="space-y-3">
				{decisions?.map((d) => (
					<DecisionCard key={d.id} d={d} />
				))}
			</div>
		</div>
	);
}

function DecisionCard({ d }: { d: DecisionDTO }) {
	const isFutureFollowUp = d.followUpDate && new Date(d.followUpDate) >= new Date();
	const isOverdue = d.followUpDate && new Date(d.followUpDate) < new Date();
	const cat = d.category || "other";
	const catColor = CATEGORY_COLORS[cat] ?? CATEGORY_COLORS.other;
	const catLabel = CATEGORY_LABELS[cat] ?? cat;

	return (
		<div className="border border-zinc-800 rounded-lg bg-zinc-900/40 p-4">
			<div className="flex items-start gap-3">
				<span className={`text-xs px-2 py-0.5 rounded border ${catColor} whitespace-nowrap mt-0.5`}>
					{catLabel}
				</span>
				<div className="flex-1 min-w-0">
					<div className="text-sm text-zinc-100">{d.summary}</div>
					<div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-zinc-500">
						{d.businesses.length > 0 && (
							<span>
								<span className="text-zinc-600">businesses:</span> {d.businesses.join(", ")}
							</span>
						)}
						{d.gate && (
							<span>
								<span className="text-zinc-600">gate:</span> {d.gate}
							</span>
						)}
						{d.runwayMonths !== null && (
							<span>
								<span className="text-zinc-600">runway:</span> {d.runwayMonths}m
							</span>
						)}
						<span>
							<span className="text-zinc-600">recorded:</span>{" "}
							{new Date(d.ts).toLocaleDateString()}
						</span>
						{d.followUpDate && (
							<span className={isOverdue ? "text-red-400" : isFutureFollowUp ? "text-amber-300" : ""}>
								<span className="text-zinc-600">follow-up:</span> {d.followUpDate}
								{isOverdue ? " (overdue)" : ""}
							</span>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
